import { useState } from 'react';
import { useInView } from '../../../hooks/useInView';

type FAQItem = {
  q: string;
  a: string;
  category: string;
};

const faqs: FAQItem[] = [
  {
    category: '料金・費用',
    q: '初期費用はかかりますか？',
    a: '初期費用は一切かかりません。月額費用のみでご利用いただけます。また、解約時の違約金もございませんので、安心してお試しいただけます。',
  },
  {
    category: '料金・費用',
    q: '途中でプランを変更することはできますか？',
    a: 'はい、可能です。採用活動の状況やご予算に応じて、月単位でプランの変更が可能です。変更のご希望は翌月から反映いたします。',
  },
  {
    category: '料金・費用',
    q: '料金に含まれるものを教えてください。',
    a: '月額料金にはターゲット設計・文面作成・スカウト送信・レポート提供がすべて含まれます。媒体の利用料（OfferBox / dodaキャンパスの契約費）は別途お客様にてご用意いただく必要があります。',
  },
  {
    category: '契約・期間',
    q: '最低契約期間はありますか？',
    a: '最低3ヶ月からのご契約となります。スカウト採用は効果が出るまでに一定の期間とデータ蓄積が必要なため、3ヶ月を最短期間として設定しています。',
  },
  {
    category: '契約・期間',
    q: '途中解約はできますか？',
    a: '最低契約期間（3ヶ月）経過後は、1ヶ月前のご通知にて解約が可能です。解約違約金は発生しません。',
  },
  {
    category: 'サポート体制',
    q: '担当者は毎回同じ人が対応してくれますか？',
    a: 'はい。ご契約いただいた企業様ごとに専任の担当者がアサインされます（プレミアムプラン）。スタンダードプランは担当チーム制となりますが、引き継ぎの手間なくご対応いたします。',
  },
  {
    category: 'サポート体制',
    q: '進捗報告はどのくらいの頻度で受け取れますか？',
    a: 'スタンダード・プレミアムプランでは毎週のレポートをお届けし、週次MTGにて数値のご説明と改善提案を実施します。ライトプランは月次レポートの提供となります。',
  },
  {
    category: 'サポート体制',
    q: '媒体のアカウントは自社で用意する必要がありますか？',
    a: 'はい。OfferBox・dodaキャンパスの企業アカウントはお客様ご自身でご契約いただく必要があります。アカウント開設のサポートについてはお気軽にご相談ください。',
  },
  {
    category: '効果・実績',
    q: '返信率に保証はありますか？',
    a: '特定の数値保証は設けておりませんが、業界平均（約2〜3%）を上回る返信率の実現を目標に、継続的な改善を実施しています。週次PDCAにより、送信内容・ターゲット・タイミングを柔軟に調整します。',
  },
  {
    category: '効果・実績',
    q: 'OfferBox・dodaキャンパス以外の媒体にも対応できますか？',
    a: '現在はOfferBox・dodaキャンパスの2媒体に特化してサービスを提供しています。他媒体への対応ご希望については、まずはお問い合わせにてご相談ください。',
  },
];

const categories = ['料金・費用', '契約・期間', 'サポート体制', '効果・実績'];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('料金・費用');
  const { ref, inView } = useInView();

  const filtered = faqs.filter((f) => f.category === activeCategory);

  return (
    <section id="faq" className="py-10 md:py-20 bg-white">
      <div ref={ref} className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          className={`text-center mb-8 md:mb-12 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-3 block">
            FAQ
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2e4a] leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            よくあるご質問
          </h2>
          <p className="text-[#666] text-sm mt-4 leading-relaxed">
            ご導入前によく寄せられるご質問をまとめました。
          </p>
        </div>

        {/* Category Tabs */}
        <div
          className={`flex flex-wrap gap-2 justify-center mb-8 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: inView ? '200ms' : '0ms' }}
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-[#1a2e4a] text-white'
                  : 'bg-[#f7f5f2] text-[#666] hover:bg-[#ebe8e4]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div
          className={`space-y-3 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: inView ? '350ms' : '0ms' }}
        >
          {filtered.map((faq, i) => {
            const globalIndex = faqs.indexOf(faq);
            const isOpen = openIndex === globalIndex;
            return (
              <div
                key={faq.q}
                className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                  isOpen ? 'border-[#1a2e4a]/30 bg-[#f7f5f2]' : 'border-gray-100 bg-white hover:border-[#1a2e4a]/20'
                }`}
                style={{ transitionDelay: inView ? `${i * 60 + 400}ms` : '0ms' }}
              >
                <button
                  onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                  className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left cursor-pointer"
                >
                  <span
                    className={`text-sm font-medium leading-snug ${isOpen ? 'text-[#1a2e4a]' : 'text-[#333]'}`}
                  >
                    {faq.q}
                  </span>
                  <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-colors duration-200 ${isOpen ? 'bg-[#1a2e4a]' : 'bg-[#f0f4f9]'}`}>
                    <i className={`text-sm transition-transform duration-300 ${isOpen ? 'ri-subtract-line text-white rotate-0' : 'ri-add-line text-[#1a2e4a]'}`} />
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                >
                  <p className="px-5 sm:px-6 pb-5 text-sm text-[#555] leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-8 sm:mt-12 text-center transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ transitionDelay: inView ? '600ms' : '0ms' }}
        >
          <p className="text-sm text-[#666] mb-4">解決しない疑問はお気軽にご相談ください</p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const t = document.querySelector('#contact');
              if (t) t.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-[#1a2e4a] text-white text-sm font-medium px-7 py-3 rounded-full hover:bg-[#263f66] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line" />
            お問い合わせする
          </a>
        </div>
      </div>
    </section>
  );
}
